import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import type {AxiosError} from 'axios'
import {PerPageOneHundred} from '../constants/perPageConstants'
import {apiClient} from '../restClient'
import {useAppStore} from '../store/appStore'
import {wt} from '@npm-questionpro/wick-ui-lib'
import {GLOBAL} from '../Translation/English/global'
import type {
  IDialog,
  IDialogAnalytics,
  IDialogDetails,
  IDialogUserAvatarSaveResponse,
  IFlaggedResponses,
  IResponseStat,
  IRestrictedUser,
  ITopInteractions,
} from '../types/dialogTypes'
import {
  ADD_DIALOG_RESPONSE_REACTION_ENDPOINT,
  ADD_DIALOG_RESPONSE_REPLY_ENDPOINT,
  CREATE_DIALOG_ENDPOINT,
  CREATE_DIALOG_RESPONSE_ENDPOINT,
  DELETE_DIALOG_RESPONSE_ENDPOINT,
  DELETE_DIALOG_RESPONSE_REPLY_ENDPOINT,
  DIALOG_USER_AVATAR_ENDPOINT,
  GET_DIALOG_ANALYTICS_ENDPOINT,
  GET_DIALOG_COUNT_ENDPOINT,
  GET_DIALOG_DETAILS_ENDPOINT,
  GET_DIALOGS_ENDPOINT,
  GET_FLAGGED_RESPONSES_ENDPOINT,
  GET_HIGHLIGHTED_RESPONSES_ENDPOINT,
  GET_SAVED_DIALOG_ANALYTICS_ENDPOINT,
  GET_TOP_INTERACTIONS_ENDPOINT,
  PATCH_FLAGGED_REPLIES_ENDPOINT,
  PATCH_FLAGGED_RESPONSES_ENDPOINT,
  UPDATE_DIALOG_ENDPOINT,
  UPDATE_DIALOG_RESPONSE_ENDPOINT,
} from './endPoints'

interface IDialogMessageResponse {
  message?: string
}

interface IDialogAvatarStatusResponse {
  username: string
}

interface IDialogDetailsApiResponse {
  dialog: IDialog
  dislikesCount: number
  likesCount: number
  totalResponsesCount: number
  totalRepliesCount: number
  totalParticipantsCount: number
  restrictedUsers: IRestrictedUser[]
}

interface IRawTopInteractionsResponse {
  mostLiked?: IResponseStat[]
  mostDisliked?: IResponseStat[]
  mostCommented?: IResponseStat[]
}

export const formatDetails = (
  data: IDialogDetailsApiResponse,
): IDialogDetails => ({
  ...data.dialog,
  dislikesCount: data.dislikesCount ?? 0,
  likesCount: data.likesCount ?? 0,
  totalResponsesCount: data.totalResponsesCount ?? 0,
  totalRepliesCount: data.totalRepliesCount ?? 0,
  totalParticipantsCount: data.totalParticipantsCount ?? 0,
  restrictedUsers: data.restrictedUsers ?? [],
})

const normalizeTopInteractions = (
  data: IRawTopInteractionsResponse,
): ITopInteractions => {
  const normalizeList = (list: IResponseStat[] | undefined): IResponseStat[] =>
    (list ?? []).map(item => ({
      ...item,
      likeCount: item.likeCount ?? 0,
      dislikeCount: item.dislikeCount ?? 0,
      replyCount: item.replyCount ?? 0,
    }))
  return {
    mostLiked: normalizeList(data.mostLiked),
    mostDisliked: normalizeList(data.mostDisliked),
    mostCommented: normalizeList(data.mostCommented),
  }
}

const fetchDialogCount = (): Promise<number> =>
  apiClient.get(GET_DIALOG_COUNT_ENDPOINT).then(r => r.data?.total ?? 0)

const fetchDialogs = (
  isArchived: boolean,
  workforceId: string,
  page: number,
): Promise<{dialogs: IDialog[]; total?: number}> =>
  apiClient
    .get(GET_DIALOGS_ENDPOINT, {
      params: {
        isArchived,
        workforceID: workforceId,
        limit: PerPageOneHundred,
        offset: (page - 1) * PerPageOneHundred,
      },
    })
    .then(r => r.data)

const fetchDialogDetails = (dialogId: string): Promise<IDialogDetails> =>
  apiClient
    .get(GET_DIALOG_DETAILS_ENDPOINT(dialogId))
    .then(r => formatDetails(r.data as IDialogDetailsApiResponse))

const fetchFlaggedResponses = (dialogId: string): Promise<IFlaggedResponses> =>
  apiClient.get(GET_FLAGGED_RESPONSES_ENDPOINT(dialogId)).then(r => r.data)

const fetchHighlightedResponses = (
  dialogId: string,
): Promise<IResponseStat[]> =>
  apiClient.get(GET_HIGHLIGHTED_RESPONSES_ENDPOINT(dialogId)).then(r => r.data)

const fetchTopInteractions = (dialogId: string): Promise<ITopInteractions> =>
  apiClient
    .get(`${GET_TOP_INTERACTIONS_ENDPOINT}/${dialogId}`)
    .then(r => normalizeTopInteractions(r.data as IRawTopInteractionsResponse))

const fetchDialogAnalytics = async (
  dialogId: string,
): Promise<IDialogAnalytics> => {
  const saved = await apiClient
    .get(GET_SAVED_DIALOG_ANALYTICS_ENDPOINT, {params: {dialogId}})
    .then(r => r.data)
  if (saved?.generatedAt) return saved
  return apiClient
    .get(GET_DIALOG_ANALYTICS_ENDPOINT, {params: {dialogId}})
    .then(r => r.data)
}

const fetchDialogUserAvatar = (): Promise<string[]> =>
  apiClient.get(DIALOG_USER_AVATAR_ENDPOINT).then(r => r.data)

export const dialogApi = {
  useDialogCount: () =>
    useQuery({
      queryKey: ['dialogs', 'count'],
      queryFn: fetchDialogCount,
    }),

  useDialogs: (isArchived: boolean, workforceId: string, page: number) =>
    useQuery({
      queryKey: ['dialogs', isArchived, workforceId, page],
      queryFn: () => fetchDialogs(isArchived, workforceId, page),
      enabled: !!workforceId,
      placeholderData: keepPreviousData,
    }),

  useDialogDetails: (dialogId: string) =>
    useQuery({
      queryKey: ['dialog', dialogId],
      queryFn: () => fetchDialogDetails(dialogId),
      enabled: !!dialogId,
    }),

  useFlaggedResponses: (dialogId: string) =>
    useQuery({
      queryKey: ['dialog', dialogId, 'flagged'],
      queryFn: () => fetchFlaggedResponses(dialogId),
      enabled: !!dialogId,
    }),

  useHighlightedResponses: (dialogId: string) =>
    useQuery({
      queryKey: ['dialog', dialogId, 'highlights'],
      queryFn: () => fetchHighlightedResponses(dialogId),
      enabled: !!dialogId,
    }),

  useTopInteractions: (dialogId: string) =>
    useQuery({
      queryKey: ['dialog', dialogId, 'interactions'],
      queryFn: () => fetchTopInteractions(dialogId),
      enabled: !!dialogId,
    }),

  useDialogAnalytics: (dialogId: string, enabled = true) =>
    useQuery({
      queryKey: ['dialog', dialogId, 'analytics'],
      queryFn: () => fetchDialogAnalytics(dialogId),
      enabled: !!dialogId && enabled,
    }),

  useDialogUserAvatar: () =>
    useQuery({
      queryKey: ['dialog', 'avatars'],
      queryFn: fetchDialogUserAvatar,
    }),

  useCreateDialog: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: (payload: {initialState: Record<string, unknown>}) =>
        apiClient
          .post(CREATE_DIALOG_ENDPOINT, payload.initialState)
          .then(r => r.data),
      onSuccess: () => void qc.invalidateQueries({queryKey: ['dialogs']}),
      onError: (err: AxiosError<{message?: string}>) =>
        useAppStore
          .getState()
          .showError(
            err.response?.data?.message ||
              err.message ||
              wt(GLOBAL.somethingWentWrong),
          ),
    })
  },

  useDeleteDialog: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: ({id}: {id: string}) =>
        apiClient.delete(GET_DIALOG_DETAILS_ENDPOINT(id)).then(r => r.data),
      onSuccess: (data: IDialogMessageResponse) => {
        void qc.invalidateQueries({queryKey: ['dialogs']})
        useAppStore.getState().showSuccess(data?.message || '')
      },
      onError: (err: AxiosError<{message?: string}>) =>
        useAppStore
          .getState()
          .showError(
            err.response?.data?.message ||
              err.message ||
              'Delete dialog failed',
          ),
    })
  },

  useUpdateDialog: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: (action: Record<string, unknown>) =>
        apiClient
          .patch(UPDATE_DIALOG_ENDPOINT, action)
          .then(r => r.data as IDialogMessageResponse),
      onSuccess: (data, variables) => {
        void qc.invalidateQueries({queryKey: ['dialogs']})
        if (variables.id) {
          void qc.invalidateQueries({
            queryKey: ['dialog', String(variables.id)],
          })
        }
        if (variables.actionTakenTopics == null) {
          useAppStore.getState().showSuccess(data?.message || '')
        }
      },
      onError: (err: AxiosError<{message?: string}>) =>
        useAppStore
          .getState()
          .showError(
            err.response?.data?.message ||
              err.message ||
              'Update dialog failed',
          ),
    })
  },

  useAddDialogResponse: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: (action: Record<string, unknown>) =>
        apiClient
          .post(CREATE_DIALOG_RESPONSE_ENDPOINT, action)
          .then(r => r.data),
      onSuccess: (_data, variables) => {
        if (variables.dialogId) {
          void qc.invalidateQueries({
            queryKey: ['dialog', String(variables.dialogId)],
          })
        }
      },
      onError: () => useAppStore.getState().showError('Submit response failed'),
    })
  },

  useUpdateDialogResponse: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: ({
        id,
        dialogId: _dialogId,
        ...rest
      }: {
        id: string
        dialogId?: string
        [key: string]: unknown
      }) =>
        apiClient
          .put(UPDATE_DIALOG_RESPONSE_ENDPOINT(String(id)), {id, ...rest})
          .then(r => r.data),
      onSuccess: (_data, variables) => {
        if (variables.dialogId) {
          void qc.invalidateQueries({queryKey: ['dialog', variables.dialogId]})
        } else {
          void qc.invalidateQueries({queryKey: ['dialog']})
        }
      },
      onError: (err: AxiosError<{message?: string}>) =>
        useAppStore
          .getState()
          .showError(
            err.response?.data?.message ||
              err.message ||
              'Update response failed',
          ),
    })
  },

  useDeleteDialogResponse: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: ({
        id,
        dialogId: _dialogId,
      }: {
        id: string
        dialogId?: string
      }) =>
        apiClient
          .delete(DELETE_DIALOG_RESPONSE_ENDPOINT(String(id)))
          .then(r => r.data),
      onSuccess: (_data, variables) => {
        if (variables.dialogId) {
          void qc.invalidateQueries({queryKey: ['dialog', variables.dialogId]})
        } else {
          void qc.invalidateQueries({queryKey: ['dialog']})
        }
      },
      onError: (err: AxiosError<{message?: string}>) =>
        useAppStore
          .getState()
          .showError(
            err.response?.data?.message ||
              err.message ||
              'Delete response failed',
          ),
    })
  },

  useAddDialogResponseReaction: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: (action: {
        responseId: string
        dialogId?: string
        [key: string]: unknown
      }) =>
        apiClient
          .post(
            ADD_DIALOG_RESPONSE_REACTION_ENDPOINT(action.responseId),
            action,
          )
          .then(r => r.data),
      onSuccess: (_data, variables) => {
        if (variables.dialogId) {
          void qc.invalidateQueries({queryKey: ['dialog', variables.dialogId]})
        }
      },
    })
  },

  useSaveDialogUserAvatar: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: (
        action: Record<string, unknown>,
      ): Promise<IDialogUserAvatarSaveResponse> =>
        apiClient.post(DIALOG_USER_AVATAR_ENDPOINT, action).then(r => r.data),
      onSuccess: () => void qc.invalidateQueries({queryKey: ['dialog']}),
    })
  },

  useUpdateDialogUserAvatarStatus: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: (action: {
        id: string
        isRestricted: boolean
        dialogId?: string
        [key: string]: unknown
      }) =>
        apiClient
          .patch(DIALOG_USER_AVATAR_ENDPOINT, action)
          .then(r => r.data as IDialogAvatarStatusResponse),
      onSuccess: (data, variables) => {
        const message = `${data.username}  ${
          variables.isRestricted
            ? 'has been restricted.'
            : 'is no longer restricted.'
        }`
        useAppStore.getState().showSuccess(message)
        if (variables.dialogId) {
          void qc.invalidateQueries({
            queryKey: ['dialog', variables.dialogId, 'flagged'],
          })
          void qc.invalidateQueries({queryKey: ['dialog', variables.dialogId]})
        }
      },
      onError: (err: AxiosError<{message?: string}>) =>
        useAppStore
          .getState()
          .showError(
            err.response?.data?.message ||
              err.message ||
              'Update user status failed',
          ),
    })
  },

  useAddDialogResponseReply: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: (action: {dialogId?: string; [key: string]: unknown}) =>
        apiClient
          .post(ADD_DIALOG_RESPONSE_REPLY_ENDPOINT, action)
          .then(r => r.data),
      onSuccess: (_data, variables) => {
        if (variables.dialogId) {
          void qc.invalidateQueries({queryKey: ['dialog', variables.dialogId]})
        }
      },
      onError: () => useAppStore.getState().showError('Submit reply failed'),
    })
  },

  useDeleteDialogResponseReply: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: ({
        id,
        dialogId: _dialogId,
      }: {
        id: string
        dialogId?: string
      }) =>
        apiClient
          .delete(DELETE_DIALOG_RESPONSE_REPLY_ENDPOINT(id))
          .then(r => r.data),
      onSuccess: (_data, variables) => {
        if (variables.dialogId) {
          void qc.invalidateQueries({queryKey: ['dialog', variables.dialogId]})
        }
      },
      onError: (err: AxiosError<{message?: string}>) =>
        useAppStore
          .getState()
          .showError(
            err.response?.data?.message || err.message || 'Delete reply failed',
          ),
    })
  },

  usePatchFlaggedResponse: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: ({
        id,
        dialogId: _dialogId,
        ...rest
      }: {
        id: string
        dialogId?: string
        [key: string]: unknown
      }) =>
        apiClient
          .patch(PATCH_FLAGGED_RESPONSES_ENDPOINT(id), {id, ...rest})
          .then(r => r.data),
      onSuccess: (_data, variables) => {
        if (variables.dialogId) {
          void qc.invalidateQueries({
            queryKey: ['dialog', variables.dialogId, 'flagged'],
          })
        }
      },
    })
  },

  usePatchFlaggedReplies: () => {
    const qc = useQueryClient()
    return useMutation({
      mutationFn: ({
        id,
        dialogId: _dialogId,
        ...rest
      }: {
        id: string
        dialogId?: string
        [key: string]: unknown
      }) =>
        apiClient
          .patch(PATCH_FLAGGED_REPLIES_ENDPOINT(id), {id, ...rest})
          .then(r => r.data),
      onSuccess: (_data, variables) => {
        if (variables.dialogId) {
          void qc.invalidateQueries({
            queryKey: ['dialog', variables.dialogId, 'flagged'],
          })
        }
      },
    })
  },
} as const
